import { UA, WebSocketInterface } from 'jssip';
import { CallStatus } from '../types/enums';
import { ParametersObject, AudioElement } from '../types/types';
import api from './api';

interface JsSipConfig {
  pcConfig: RTCConfiguration;
  rtcConstraints: any;
  ws_servers: { ws_uri: string }[];
  version?: string;
  domain: string;
  ice_candidate_timeout?: number;
  rp?: string;
}

interface CallDevices {
  inputDevice: string;
  outputDevice: string;
}

export interface JsSipControllerListener {
  onNewDtmf?: (tone: string | number) => void;
  onMicMute?: () => void;
  onMicUnmute?: () => void;
  onSpeakerMute?: (status: boolean) => void;
  onStatusUpdate?: (status: CallStatus) => void;
  onCallDurationUpdate?: (duration: number) => void;
  onEnd?: () => void;
  onError?: () => void;
}

export class JsSipController {
  private ua: UA | null = null;
  private session: any = null;
  private listeners: JsSipControllerListener[] = [];
  private _remoteAudioElement: AudioElement | null = null;
  private callStartTime: Date | null = null;
  private callDurationTimer: NodeJS.Timer | null = null;
  private token: string = '';
  private waitingIceCandidates = false;
  private disconnectCount = 0;

  constructor(
    private readonly sessionId: string,
    private config: JsSipConfig,
    private parameters: ParametersObject,
    private devices: CallDevices,
  ) {
  }

  public addListener(listener: JsSipControllerListener): void {
    this.listeners.push(listener);
  }

  public removeListener(listener: JsSipControllerListener): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  private notifyListeners(event: keyof JsSipControllerListener, data?: any): void {
    this.listeners.forEach((listener) => {
      if (listener[event]) {
        listener[event]!(data as never);
      }
    });
  }

  public async initialize() {
    try {
      const configuration = {
        ...this.config,
        sockets: this.config.ws_servers.map((wss) => new WebSocketInterface(wss.ws_uri)),
        register: true,
        user_agent: 'SP-1.0.0/C-' + (this.config.version || 1),
        uri: localStorage.getItem("from") || `sip:${this.sessionId}@${this.config.domain}`,
      };

      this.token = await api.getToken();

      this.ua = new UA(configuration);

      this.ua.registrator().setExtraHeaders([
        `Accesstoken: ${this.token}`
      ]);
      this.attachEventListeners();
      this.ua.start();
    } catch (e) {
      console.error('JsSIP: initialize error', e);
      this.notifyListeners('onError');
    }
  }

  public set remoteAudioElement(element: AudioElement) {
    this._remoteAudioElement = element;
  }

  private attachEventListeners(): void {
    if (!this.ua) return;

    this.ua.on('connected', (args) => {
      console.log('JsSIP: connected ', args);
      this.disconnectCount = 0;
    });
    this.ua.on('disconnected', (args) => {
      console.log('JsSIP: disconnected ', args);
      args.error && this.disconnectCount++;
      if (this.disconnectCount >= this.config.ws_servers.length) {
        console.log('JsSIP: The number of reconnection attempts has been exceeded, calling terminate with an error');
        this.terminate(true);
      }
    });
    this.ua.on('registered', (args) => {
      console.log('JsSIP: registered ', args);
      this.call(this.config.rp!, this.devices);
    });
    this.ua.on('registrationFailed', (args) => {
      console.log('JsSIP: registrationFailed ', args);
      this.terminate(true);
    });
    this.ua.on('unregistered', (args) => {
      console.log('JsSIP: unregistered ', args);
    });
    this.ua.on('newRTCSession', this.onNewRTCSession);
  }

  private onNewRTCSession = (e: any) => {
    console.log('JsSIP: newRTCSession ', e);

    this.session = e.session;
  }

  private async updateView(
    element: AudioElement | null,
    stream: MediaStream | null,
    sinkId?: string,
  ) {
    if (element) {
      element.srcObject = stream;
      if (sinkId && typeof element.setSinkId === 'function') {
        try {
          await element.setSinkId(sinkId)
        } catch (error) {
          console.error('JsSIP: error setting sink ID:', error);
        }
      }
    }
  }

  private onCallEnd = () => {
    if (this.callDurationTimer) {
      clearInterval(this.callDurationTimer);
      this.callDurationTimer = null;
    }
    this.callStartTime = null;

    this.notifyListeners('onEnd');
  }

  private callEventHandlers = {
    peerconnection: (event: any) => {
      console.log('JsSIP: RTCSession peerconnection ', event);
      event.peerconnection.ontrack = (evt: any) => {
        if (evt.streams && evt.streams[0]) {
          this.updateView(this._remoteAudioElement, evt.streams[0], this.devices.outputDevice);
        } else {
          this.updateView(this._remoteAudioElement, new MediaStream(evt.track), this.devices.outputDevice);
        }
      }
    },
    connecting: (event: any) => {
      console.log('JsSIP: RTCSession connecting ', event);
    },
    sending: (event: any) => {
      console.log('JsSIP: RTCSession sending ', event);
    },
    progress: (event: any) => {
      console.log('JsSIP: RTCSession progress ', event);
    },
    accepted: (data: any) => {
      console.log('JsSIP: RTCSession accepted ', data);
    },
    icecandidate: (event: any) => {
      console.log('JsSIP: RTCSession icecandidate ', event);
      if (!this.waitingIceCandidates) {
        setTimeout(
          () => event.ready(),
          this.config['ice_candidate_timeout'] || 1000,
        );
        this.waitingIceCandidates = true;
      }
      console.log(
        new Date().toLocaleString() + ' Icecandidate',
        event.candidate,
      );
    },
    confirmed: (data: any) => {
      console.log('JsSIP: RTCSession confirmed ', data);

      this.notifyListeners('onStatusUpdate', CallStatus.CONFIRMED);
      this.callStartTime = new Date();
      this.callDurationTimer = setInterval(() => {
        if (this.callStartTime) {
          const duration = new Date().getTime() - this.callStartTime.getTime();
          this.notifyListeners('onCallDurationUpdate', duration);
        }
      }, 1000);
    },
    newDTMF: (data: any) => {
      if (data.originator === 'local') {
        this.notifyListeners('onNewDtmf', data.dtmf.tone);
      }
    },
    muted: () => {
      this.notifyListeners('onMicMute');
    },
    unmuted: () => {
      this.notifyListeners('onMicUnmute');
    },
    ended: (data: any) => {
      console.log('JsSIP: RTCSession ended ', data);
      this.terminate();
    },
    failed: (data: any) => {
      console.log('JsSIP: RTCSession failed ', data);
      this.terminate(true);
    },
  }

  public call(target: string, devices: CallDevices): void {
    if (!this.ua) return;

    this.ua.call(target, {
      eventHandlers: this.callEventHandlers,
      mediaConstraints: {
        audio: devices.inputDevice && devices.inputDevice !== 'default' ?
          { deviceId: { exact: devices.inputDevice } } : true,
        video: false,
      },
      pcConfig: this.config.pcConfig,
      rtcConstraints: this.config.rtcConstraints,
      extraHeaders: [
        `Accesstoken: ${this.token}`,
        ...Object.entries(this.parameters).map(([key, value]) => `${key}: ${value}`),
      ]
    });
  }

  public terminate(error?: boolean): void {
    this.ua?.isRegistered() && this.ua?.unregister();
    this.ua?.stop();
    error ?
      this.notifyListeners('onError') :
      this.onCallEnd();
  }

  public micMute(): void {
    if (!this.session) return;

    if (this.session.isMuted().audio) {
      this.session.unmute({ audio: true });
    } else {
      this.session.mute({ audio: true });
    }
  }

  public speakerMute(mute: boolean): void {
    if (!this._remoteAudioElement) return;

    this._remoteAudioElement.muted = mute;
    this.notifyListeners('onSpeakerMute', mute);
  }

  public sendDtmf(item: string): void {
    this.session?.sendDTMF(item, {
      duration: 100,
    });
  }
}